import {
  AnalyticsTrackingEvent,
  BuyBoxDialog,
  isProductEligibleForCheckout,
  ProductRewardSearchResult,
} from '@brandclub/common-ui';
import { lazy, Suspense, type FC } from 'react';
import { useSplitTreatments } from '@splitsoftware/splitio-react';
import { useDomainConfig } from '@/api/cache/appConfigQueryOptions';
import { useCartUpdates } from '@/api/cache/cartQueryOptions';
import { useTrackActions } from '@/hooks/useTracking';
import { useBCRouting } from '@/providers/BCRoutingContextProvider';
import { HOST_DOMAIN } from '@/utils/domain';
import BagDrawer from '../BagButton/BagDrawer';
import { getPriorityEligibleProduct } from '../AddToBagButton/inpageAddToBagHelpers';

const LocationMap = lazy(() => import('./LocationMap'));

const TRACK_SOURCE = 'brandclub_dtc_inline_button';

export interface LocationMapForSkuProps {
  product: ProductRewardSearchResult;
  mobile?: boolean;
}

const LocationMapForSku: FC<LocationMapForSkuProps> = ({ product, mobile = false }) => {
  const { data: domainConfig } = useDomainConfig();
  const { mutate: updateCart } = useCartUpdates();
  const [trackAction] = useTrackActions();
  const bcRouting = useBCRouting();

  const { treatments } = useSplitTreatments({
    names: ['brandclub_checkout_disabled_domain'],
    attributes: {
      domain: window.location.hostname,
      bcSplit: (window as any).bcSplit,
    },
  });
  const disableCheckoutByDomain = treatments?.brandclub_checkout_disabled_domain?.treatment === 'disable';

  const priorityProduct = getPriorityEligibleProduct(product, domainConfig?.checkoutAllowedOfferType);

  const handleBuyClick = (p: ProductRewardSearchResult) => {
    if (!p) return;

    const eligible = isProductEligibleForCheckout(p, domainConfig?.checkoutAllowedOfferType);

    if (eligible && !disableCheckoutByDomain) {
      updateCart({
        action: 'add',
        items: [{ ...p, quantity: 1 }],
        retailerId: p.retailerId,
      });
      trackAction(AnalyticsTrackingEvent.WEB_APP_ORGANIC_ADD_TO_CART, {
        stacklineSku: p.stacklineSku,
        retailerId: p.retailerId,
        retailerSku: p.retailerSku,
        retailPrice: p.retailPrice,
        quantity: 1,
        source: TRACK_SOURCE,
      });
      BagDrawer?.show();
    } else {
      trackAction(AnalyticsTrackingEvent.OPEN_RETAILER_PRODUCT_PAGE, {
        stacklineSku: p.stacklineSku,
        retailerId: p.retailerId,
        retailerSku: p.retailerSku,
        retailPrice: p.retailPrice,
        source: TRACK_SOURCE,
      });
      bcRouting.navigate(p.retailerProductPageUrl, '_blank');
    }
  };

  return (
    <div style={{ marginTop: '30px' }} className="bc-location-map-container">
      <Suspense fallback={null}>
        <LocationMap
          mobile={mobile}
          stacklineSku={product?.stacklineSku}
          API_URL={HOST_DOMAIN}
          product={product}
          onAddProductToBag={() => {
            product?.matchedProducts?.length
              ? BuyBoxDialog.show({
                  checkoutAllowedOfferType: domainConfig?.checkoutAllowedOfferType,
                  products: [{ ...product }, ...product.matchedProducts],
                  onClick: handleBuyClick,
                  onProductPageClick: (p) => {
                    window.open(p.retailerProductPageUrl, '_blank');
                  },
                })
              : priorityProduct && handleBuyClick(priorityProduct as ProductRewardSearchResult);
          }}
        />
      </Suspense>
    </div>
  );
};

export default LocationMapForSku;
